import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Refresh, Tv, BatteryFull } from '@mui/icons-material';
import { eventBus, EventTypes } from '@core/pubsub/EventBus';
import { dashboardService } from '@/services/dashboardService';
import { ShopId } from '@core/store/slices/apiSlice.ts';

// Apex Charts with React
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { BaseWidget } from '@/components/widgets/BaseWidget.tsx';
import { useNavigate } from 'react-router-dom';
import { useSettingsStore } from '@core/store';

interface BatteryStateData {
    batteryEmpty: number;
    batteryHalf: number;
    batteryFull: number;
}

interface FastLanerBatteryStateProps {
    shopIdName: ShopId;
    heading: string;
    className?: string;
}

const FastLanerBatteryState: React.FC<FastLanerBatteryStateProps> = ({ shopIdName, heading, className }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [series, setSeries] = useState([0, 0, 0]);
    const BATTERY_FULL_THRESHOLD = 30;
    const BATTERY_HALF_THRESHOLD = 20;
    const navigate = useNavigate();
    const { darkMode } = useSettingsStore();

    const getBatteryStateData = (response: any): BatteryStateData => {
        let batteryEmpty = 0;
        let batteryHalf = 0;
        let batteryFull = 0;
        const groups = response?.data ?? [];

        groups.forEach((group: any) => {
            (group.trolleys ?? []).forEach((trolley: any) => {
                const { batteryState } = trolley;
                if (batteryState != null) {
                    const batteryStateNumber = batteryState.slice(0, -1);
                    const batteryStateInt = parseInt(batteryStateNumber, 10);
                    if (batteryStateInt >= BATTERY_FULL_THRESHOLD) {
                        batteryFull += 1;
                    } else if (batteryStateInt >= BATTERY_HALF_THRESHOLD) {
                        batteryHalf += 1;
                    } else {
                        batteryEmpty += 1;
                    }
                } else {
                    batteryEmpty += 1;
                }
            });
        });

        return {
            batteryEmpty,
            batteryHalf,
            batteryFull
        };
    };

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const fastLanerTrolleyData = await dashboardService.getFastLanerTrolleyData(shopIdName.fastLanerGroupIds);
            const batteryState = getBatteryStateData(fastLanerTrolleyData);
            setSeries([batteryState.batteryEmpty, batteryState.batteryHalf, batteryState.batteryFull]);
        } catch (error) {
            console.error('Failed to load FastLaner battery state:', error);
            setSeries([0, 0, 0]);
        } finally {
            setLoading(false);
        }
    }, [shopIdName.fastLanerGroupIds]);

    useEffect(() => {
        load();

        const unsubscribe = eventBus.on(EventTypes.WIDGETS_RELOAD, load);

        return unsubscribe;
    }, [load]);

    const menuActions = [
        {
            text: t('refresh'),
            icon: <Refresh fontSize="small" />,
            onClick: load
        },
        {
            text: t('monitoring'),
            icon: <Tv fontSize="small" />,
            onClick: () => {
                navigate(`/statistics/fastLaner/health?shopId=${shopIdName.id}`);
            }
        }
    ];

    const options: ApexOptions = {
        chart: {
            type: 'donut',
            height: 300,
            toolbar: {
                show: false
            },
            background: 'transparent',
            fontFamily: 'Wanzl_Etelka Light, Roboto, sans-serif'
        },
        labels: [t('batteryEmpty'), t('batteryHalf'), t('batteryFull')],
        colors: darkMode
            ? ['#1781A5', '#CE8E4E', '#ABB32E', '#6C8989', '#8355B7', '#6BA5A3']
            : ['#0099CD', '#F49739', '#C6D300', '#659999', '#9D60E2', '#7ACDC9'],
        fill: {
            type: 'solid',
            opacity: 0.93
        },
        dataLabels: {
            enabled: true,
            formatter: function (val: number, opts: any) {
                return opts.w.globals.series[opts.seriesIndex];
            },
            style: {
                fontSize: '14px'
            }
        },
        stroke: {
            width: 0
        },
        plotOptions: {
            pie: {
                customScale: 0.85,
                startAngle: -100,
                endAngle: 100,
                donut: {
                    size: '72%',
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            offsetY: 70
                        },
                        value: {
                            show: true,
                            offsetY: -5,
                            fontSize: '35px'
                        },
                        total: {
                            show: true,
                            label: t('amountOfFastLaners'),
                            fontSize: '20px',
                            fontWeight: 420,
                            formatter: () => series.reduce((sum, val) => sum + val, 0).toString()
                        }
                    }
                }
            }
        },
        legend: {
            show: true,
            position: 'bottom',
            offsetY: -50
        },
        theme: {
            mode: 'light'
        }
    };

    return (
        <BaseWidget
            heading={heading}
            icon={<BatteryFull />}
            className={className}
            customActions={menuActions}
            loading={loading}
        >
            <div style={{ marginTop: '-10px', marginBottom: '-40px' }}>
                <Chart options={options} series={series} type="donut" height={300} />
            </div>
        </BaseWidget>
    );
};

export default FastLanerBatteryState;
